'use client';
import { trackEvent } from '@/shared/components/GoogleMetrika/GoogleMetrika';
import { useFeedbackStore } from '@/shared/store/feedbackStore';
import { useTranslations } from 'next-intl';
import { ChangeEvent, useState } from 'react';

type FieldErrors = Partial<Record<
  'full_name' | 'company_name' | 'phone_number' | 'email' | 'company_specialization' | 'details',
  string
>>;

export function useFeedbackModal() {
  const {
    formData,
    isLoading,
    error,
    success,
    postFeedback,
    resetForm,
    setFormField,
  } = useFeedbackStore();

  const t = useTranslations('ui.feedbackForm');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors | null>(null);

  const CHAR_LIMITS: Record<string, number> = {
    full_name: 50,
    company_name: 30,
    company_position: 30,
    phone_number: 20,
    email: 100,
    company_specialization: 500,
    details: 500,
  };

  const validateEmail = (email: string): boolean =>
    email.includes('@') &&
    email.includes('.') &&
    email.indexOf('@') < email.lastIndexOf('.');

  const handleInputChange =
    (field: keyof typeof formData) =>
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let value = e.target.value;

        const limit = CHAR_LIMITS[field];
        if (limit && value.length > limit) {
          value = value.slice(0, limit);
        }

        if (field === 'phone_number') {
          const phoneRegex = /^\+?$|^\+?\d+$/;
          if (!phoneRegex.test(value)) {
            for (let i = value.length; i >= 0; i--) {
              const slice = value.slice(0, i);
              if (phoneRegex.test(slice)) {
                value = slice;
                break;
              }
            }
          }
        }

        if (field === 'email') {
          const emailRegex = /^[a-zA-Z0-9._!#$%&'*+/=?^`{|}~-]*@?[a-zA-Z0-9.-]*$/;
          if (!emailRegex.test(value)) {
            for (let i = value.length; i >= 0; i--) {
              const slice = value.slice(0, i);
              if (emailRegex.test(slice)) {
                value = slice;
                break;
              }
            }
          }
        }

        setFormField(field, value);
        if (fieldErrors?.[field]) {
          setFieldErrors((prev) => (prev ? { ...prev, [field]: undefined } : null));
        }
      };

  const dataValidation = (): boolean => {
    const newErrors: FieldErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = t('validation.required');
    }

    // if (!formData.company_name.trim()) {
    //   newErrors.company_name = t('validation.required');
    // }

    if (!formData.email.trim()) {
      newErrors.email = t('validation.required');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('validation.invalidEmail');
    }

    // if (!formData.company_specialization.trim()) {
    //   newErrors.company_specialization = t('validation.required');
    // }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    const isValid = dataValidation();
    if (!isValid) {
      setSubmitError(t('validation.fixErrors'));
      return;
    }
    setSubmitError(null);
    trackEvent('button_click_contact_modal', {
      button_name: 'contact_modal_button',
      page: window.location.pathname,
    });
    await postFeedback(formData, window.location.href);
  };

  const handleReset = () => {
    resetForm();
    setFieldErrors(null);
    setSubmitError(null);
  };

  return {
    formData,
    isLoading,
    error,
    success,
    submitError,
    fieldErrors,
    handleInputChange,
    handleSubmit,
    handleReset,
  };
}
